<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import installingInsert from './images/09-indow-windows-copypp_w1600_h1067.jpg';
	import priceChart from './images/FaceBook-Pricing-Comparisons-Gerry-Rice-D-1200x628-1.jpg';
	import windowPile from './images/Old-windows-in-a-pile-1200x800.jpg';
	import youtubeThumbnail from './images/jaAkRT-WgdU.jpeg?w=610';
</script>

<Section>
	<p>
		In your search for window solutions for your home, you may have come across
		the word “retrofit.” When it comes to windows, “retrofit” has a couple
		definitions. Retrofitting windows can refer to replacement of the window
		sash, the part of the window that holds the glass and usually slides up and
		down or side to side. Retrofitting a window can also refer to fitting an
		existing window with an attachment, such as an exterior storm window or
		interior storm window insert. In either case, the window frame will stay in
		place and not be altered.
	</p>
	<p>
		While the idea behind both types of retrofitting is to preserve as much of
		the original window as possible, the two options do have fundamental
		differences that should be discussed before you decide which is best for
		your windows.
	</p>

	<div
		class="
    mb-8 w-full overflow-x-auto
    max-md:col-span-full!
  "
	>
		<table class="w-full text-center">
			<thead class="bg-indow-grey">
				<tr>
					<th class="border border-indow-grey-light p-3 text-indow-white"
						>Category</th
					>
					<th class="border border-indow-grey-light p-3 text-indow-white"
						>Retrofit replacement</th
					>
					<th class="border border-indow-grey-light p-3 text-indow-white"
						>Retrofit Attachment</th
					>
				</tr>
			</thead>
			<tbody>
				<tr class="border bg-indow-white">
					<td class="border border-indow-grey-light px-3 py-2">Cost</td>
					<td class="border border-indow-grey-light px-3 py-2">X</td>
					<td class="border border-indow-grey-light px-3 py-2">Better</td>
				</tr>
				<tr class="border bg-indow-white">
					<td class="border border-indow-grey-light px-3 py-2"
						>Energy Performance</td
					>
					<td class="border border-indow-grey-light px-3 py-2">Good</td>
					<td class="border border-indow-grey-light px-3 py-2">Better</td>
				</tr>
				<tr class="border bg-indow-white">
					<td class="border border-indow-grey-light px-3 py-2"
						>Sound Reduction</td
					>
					<td class="border border-indow-grey-light px-3 py-2">Good</td>
					<td class="border border-indow-grey-light px-3 py-2">Better</td>
				</tr>
				<tr class="border bg-indow-white">
					<td class="border border-indow-grey-light px-3 py-2"
						>Ease of Installation</td
					>
					<td class="border border-indow-grey-light px-3 py-2">X</td>
					<td class="border border-indow-grey-light px-3 py-2">Better</td>
				</tr>
				<tr class="border bg-indow-white">
					<td class="border border-indow-grey-light px-3 py-2"
						>Eco-Friendliness</td
					>
					<td class="border border-indow-grey-light px-3 py-2">X</td>
					<td class="border border-indow-grey-light px-3 py-2">Better</td>
				</tr>
			</tbody>
		</table>
	</div>

	<h2>Retrofit Replacement vs. Retrofit Attachment: Cost</h2>
	<p>
		On average, retrofit attachments, such as interior storm window inserts, are
		75% cheaper than wood window retrofit replacements, and 50% cheaper than
		vinyl retrofit replacements. According to <a
			href="https://www.remodeling.hw.net/cost-vs-value/2019/"
			rel="external">Remodeling</a
		>, 2021 costs for a wood window replacement project was $23,219, and a vinyl
		window replacement project was $19,385. This was for 10 vinyl 3×5’ windows.
		A similar project for retrofit attachments like Standard Grade Indow window
		inserts would cost $4,500. If you consider the fact that the lifespan of
		replacement windows is around 15 years and Indow window inserts come with a
		lifetime warranty, that price difference will increase over time.
	</p>
	<Image
		src={priceChart}
		alt="Cost comparison of 19 replacement windows vs. 19 window inserts: $8,000 and $25,000, respectively"
	/>
	<h2>Retrofit Replacement vs. Retrofit Attachment: Energy Performance</h2>
	<p>
		Retrofit attachments have the advantage of improving the mean radiant room
		temperature of a space. Interior storm window inserts often use acrylic for
		their panels, as opposed to glass exterior storm windows. Acrylic does a
		better job than glass of slowing heat transfer, which helps them maintain a
		higher mean radiant room temperature.
	</p>
	<p>
		A <a href="/static/pdfs/Indow_report_March_2013_FINAL.pdf"
			>year-long pilot study</a
		> by Portland State University in several homes showed that the impact of this
		warmer surface temperature of acrylic panels allowed thermostats to be set at
		a lower temperature during winter months, while occupants remained comfortable.
	</p>
	<h2>Retrofit Replacement vs. Retrofit Attachment: Noise Performance</h2>
	<p>
		Retrofit window attachments can reduce outside noise by 70% (18.9 dBA) when
		added to single-pane and 50% (10-12 dBA) when added to double-pane windows.
		Retrofit replacement windows typically <a
			href="http://www.soundproofing101.com/window_pane_thickness.htm"
			rel="external">reduce outside noise by about 25%</a
		>. There are retrofit replacements optimized for sound reduction, but they
		are priced higher than standard replacements.
	</p>
	<Image
		src={installingInsert}
		alt="Man installing Indow window inserts. Retrofitting windows with Acoustic Grade inserts reduces noise."
	/>
	<h2>Retrofit Replacement vs. Retrofit Attachment: Ease of installation</h2>
	<p>
		Retrofit replacements should be installed by a contractor, though advanced
		DIY homeowners can find plenty of window sash replacement how to videos
		online.
	</p>
	<Youtube
		src="https://www.youtube.com/watch?v=jaAkRT-WgdU"
		posterImageSrc={youtubeThumbnail}
	/>
	<p>
		Because houses shift over time, 95% of window openings are not completely
		square. For this reason, we recommend DIY sash replacement projects to only
		the most seasoned DIYers.
	</p>
	<p>
		Interior retrofit attachments have a very straightforward installation
		process. Most are custom-fit for your window. Indow window inserts use a
		patented silicone compression tube that allows for a snug fit in your window
		frame. Installation is as simple as pressing the insert into the window
		frame and making minor adjustments to the silicone edge to get a perfect
		fit.
	</p>
	<Image
		src={windowPile}
		alt="Windows torn out for replacement. Consider ecological impact with retrofit vs. replacement windows."
	/>
	<h2>Retrofit Replacement vs. Retrofit Attachment: Eco-Friendliness</h2>
	<p>
		Vinyl windows are the more common type of retrofit replacement window. Vinyl
		windows have some shocking impacts on our environment. Vinyl windows produce<a
			href="https://www.wwf.org.uk/sites/default/files/2017-06/windows_0305.pdf"
			rel="external">43% more waste than timber windows</a
		>. Once vinyl windows have met their life expectancy of around 15 years and
		are replaced, only 3% are recycled.
	</p>
	<p>
		Indow window inserts have been lab tested and can withstand replacement and
		re-installation up to 6,000 times. These inserts are a long-term solution
		for your windows, and ensure that they will not see a landfill. Indow is
		proud to work with local recyclers within Portland for our leftover acrylic
		materials. We also donate leftover materials to organizations that upcycle
		scraps for artistic purposes.
	</p>
	<p>
		So there you have it on retrofit replacement windows vs. retrofit window
		attachments. Ready to learn more about acrylic attachment options vs. glass?
		Check out our
		<a href="/resources/blog/acrylic-vs-glass">Why We Use Acrylic vs. Glass</a> blog
		post.
	</p>
</Section>
